/**
 * Earthworks AI quoting (EW-3) and the tenant AI budget — React Query hooks.
 *
 * A run in `queued` / `processing` is polled every 3 s until the AI finishes.
 * Every write puts the API's fresh run straight into the cache, because the API
 * recomputes the draft and the screen must show the API's figures, not its own.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { setQueryDataMapped } from '../lib/queryClient';
import type { AiBudget, EwAiRun, EwAiRunPatch } from '../types/ewAiQuote';

export const ewAiKeys = {
  all: ['earthworks', 'ai-quotes'] as const,
  list: () => [...ewAiKeys.all, 'list'] as const,
  run: (id: string) => [...ewAiKeys.all, 'run', id] as const,
  budget: () => ['ai-budget'] as const,
};

const IN_FLIGHT = new Set(['queued', 'processing']);

export function useEwAiRuns(enabled = true) {
  return useQuery({
    queryKey: ewAiKeys.list(),
    queryFn: async () => {
      const d = unwrap<EwAiRun[]>((await api.earthworks.aiQuotes.list()).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    refetchInterval: (q) => ((q.state.data ?? []).some((r) => IN_FLIGHT.has(r.status)) ? 5000 : false),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwAiRun(id: string | undefined) {
  return useQuery({
    queryKey: ewAiKeys.run(id ?? ''),
    queryFn: async () => unwrap<EwAiRun>((await api.earthworks.aiQuotes.get(id!)).data),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && IN_FLIGHT.has(q.state.data.status) ? 3000 : false),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Month-to-date AI spend against the cap, and the own-key state. */
export function useAiBudget(enabled = true) {
  return useQuery({
    queryKey: ewAiKeys.budget(),
    queryFn: async () => unwrap<AiBudget>((await api.aiSettings.get()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useCreateEwAiRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (form: FormData) => unwrap<EwAiRun>((await api.earthworks.aiQuotes.create(form)).data),
    onSuccess: (run) => {
      if (run) setQueryDataMapped<EwAiRun>(qc, ewAiKeys.run(run.id), run);
      qc.invalidateQueries({ queryKey: ewAiKeys.list() });
      qc.invalidateQueries({ queryKey: ewAiKeys.budget() });
    },
  });
}

export function useUpdateEwAiRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EwAiRunPatch) => unwrap<EwAiRun>((await api.earthworks.aiQuotes.update(id, body)).data),
    onSuccess: (run) => {
      // The PATCH answer carries the recomputed draft but not the page URLs; keep those.
      if (run) setQueryDataMapped<EwAiRun>(qc, ewAiKeys.run(id), { ...(qc.getQueryData<EwAiRun>(ewAiKeys.run(id)) ?? {}), ...run } as EwAiRun);
      qc.invalidateQueries({ queryKey: ewAiKeys.list() });
    },
  });
}

export function useRetryEwAiRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap<EwAiRun>((await api.earthworks.aiQuotes.retry(id)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ewAiKeys.run(id) });
      qc.invalidateQueries({ queryKey: ewAiKeys.list() });
      qc.invalidateQueries({ queryKey: ewAiKeys.budget() });
    },
  });
}

export function useCreateQuoteFromEwAiRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap<{ quoteId: string; quoteNumber: string | null; lines: number }>((await api.earthworks.aiQuotes.createQuote(id)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ewAiKeys.run(id) });
      qc.invalidateQueries({ queryKey: ewAiKeys.list() });
      qc.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

export function useSetAiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (apiKey: string) => unwrap<AiBudget>((await api.aiSettings.setKey(apiKey)).data),
    onSuccess: (b) => { if (b) setQueryDataMapped<AiBudget>(qc, ewAiKeys.budget(), b); },
  });
}

export function useRemoveAiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap<AiBudget>((await api.aiSettings.removeKey()).data),
    onSuccess: (b) => { if (b) setQueryDataMapped<AiBudget>(qc, ewAiKeys.budget(), b); },
  });
}

/** The 402 the AI-quote doors answer at the cap, with the budget it carries. */
export function aiCapReached(err: unknown): AiBudget | null {
  const r = (err as { response?: { status?: number; data?: { code?: string; data?: AiBudget } } })?.response;
  if (r?.status === 402 && r.data?.code === 'AI_CAP_REACHED') return r.data.data ?? ({} as AiBudget);
  return null;
}
